import PremiumIcon from "@assets/header/menu-selected/icon/premium-latest.svg";
import { BrightcoveVideo } from "@components/Blocks/BrightcoveVideo/BrightcoveVideo";
import ArticleImage from "@components/Layouts/Carousel/ArticleImage";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import {
  ARTICLE_PAYWALL_CLASS,
  ARTICLE_REGWALL_CLASS,
} from "@elements/MetaTags/MetaTagsConstants";
import Paragraph from "@elements/Typography/Paragraph";
import {
  CD_LANGUAGE,
  useBHCustomEventAnalyticsCall,
} from "@hooks/useAnalyticsCall/useBHCustomEventAnalyticsCall";
import useCasScript from "@hooks/useCasScript";
import ArticleElementWrapper from "@routes/Article/Components/ArticleElementWrapper";
import { NoSSR } from "@sphtech/web2-core/components";
import { type ProcessedArticleData } from "@transformer/useOSResponse";
import { AdTargetingType, ArticleAdTargetKeys } from "@typings/Ads.d";
import {
  Attachment,
  type ProcessedArticleBody,
  ProcessedTagsContent,
  type RelatedArticles,
} from "@typings/Opensearch";
import { HandleReactionClick, ReactionProp } from "@typings/Reactions.d";
import cx from "classnames";
import React, { useState } from "react";

import ApplyParagraphAnnotations from "./ApplyParagraphAnnotations";
import { YOUTUBE_WATCH_URL } from "./Embeds/constants";
import Embed from "./Embeds/Embed";
import YouTubeEmbedCustom from "./Embeds/YouTubeEmbedCustom";
import { useAccessControl } from "./hooks/useAccessControl";
import { useAccessPremiumLabel } from "./hooks/useAccessPremiumLabel";
import { useParagraphsToRender } from "./hooks/useParagraphsToRender";
import { AdsComponents } from "./ParagraphElements/AdsComponents";
import { getAdAndBannerInsertions } from "./ParagraphElements/GetAdAndBannerInsertions";
import { InsertLineBreaks } from "./ParagraphElements/InsertLineBreaks";
import ParagraphList from "./ParagraphElements/ParagraphList";
import PullQuoteCard from "./ParagraphElements/PullQuoteCard";
import SubHead from "./ParagraphElements/SubHead";
import WrapperContainer from "./ParagraphElements/WrapperContainer";
import { PostContentSection } from "./PostContentSection";

export type ArticleParagraphProps = {
  articleId: string;
  contentAccess: LabelCardIconVariety;
  articleBody: ProcessedArticleBody | null;
  uniqueSectionName: string | undefined;
  adsUniqueSectionName: string;
  relatedArticles: RelatedArticles;
  pageNumber: number;
  attachments?: Attachment[];
  tags?: ProcessedTagsContent[];
  isInfiniteScrollArticle?: boolean;
  gsChannels?: string[];
  articleSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
  freeArticle?: ProcessedArticleData;
  visibleArticleId?: string;
  showReactions: boolean;
  reactions: ReactionProp[];
  handleReactionClick: HandleReactionClick;
  userReactions: string;
  trendingArticles?: ProcessedArticleData[];

  toggleFollow: (topicId: string, topicTitle: string) => Promise<void>;
  isFollowing: (topicId: string) => boolean;
  loadingTopicId: string | null;
  visitorId: string;
};

export default function ArticleParagraph(props: ArticleParagraphProps) {
  const { isScriptReady: isCasScriptReady } = useCasScript();
  const { showSubBanner, showGenGSubBanner, showRegBanner, showReg2Banner } =
    useAccessControl(props);
  const [showFullPara, setShowFullPara] = useState<boolean>(false);
  const { adsImu1, adsMidArticle } = AdsComponents(props);
  const { pageNumber, isInfiniteScrollArticle = false, contentAccess } = props;

  const paraToRender = useParagraphsToRender(
    isInfiniteScrollArticle,
    showFullPara,
    props.articleBody,
  );

  const bhCustomEvent = useBHCustomEventAnalyticsCall();
  const handleReadMoreClick = () => {
    setShowFullPara(true);
    bhCustomEvent(
      "read more",
      "click",
      window.location.href,
      undefined,
      undefined,
      CD_LANGUAGE.MALAY,
    );
  };

  let paragraphCount = 0;
  const { showPremiumLabel } = useAccessPremiumLabel(contentAccess);
  const totalParagraphCountOfFullArticle =
    paraToRender?.filter((element) => element.type === "paragraph").length ?? 0;

  const hasAccessControlBanner =
    showGenGSubBanner || showSubBanner || showRegBanner || showReg2Banner;

  const insertionProps = {
    adsImu1,
    adsMidArticle,
    pageNumber,
    isInfiniteScrollArticle,
    showSubBanner,
    showGenGSubBanner,
    showRegBanner,
    showReg2Banner,
    showFullPara,
    handleReadMoreClick,
    trendingArticles: props.trendingArticles,
  };

  const accessBannerParagraphCount = showGenGSubBanner ? 0 : 1;

  return (
    <div key={pageNumber} data-testid="article-body" className="article-body">
      {/* Add css selector for paywall/regwall content on article body. */}
      <div
        data-testid="article-body-with-css-selector"
        className={cx(
          contentAccess === LabelCardIconVariety.REGISTERED_USER &&
            ARTICLE_REGWALL_CLASS,
          contentAccess === LabelCardIconVariety.PREMIUM &&
            ARTICLE_PAYWALL_CLASS,
        )}
      >
        {paraToRender &&
          paraToRender.map((element, index) => {
            const bannersAndAds = hasAccessControlBanner
              ? null
              : getAdAndBannerInsertions({
                  paragraphCount,
                  ...insertionProps,
                });

            switch (element.type) {
              case "paragraph":
                paragraphCount++;
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <Paragraph
                        testid={`paragraph-${index}`}
                        className={`font-primary font-medium text-md leading-[150%] text-text-body mb-[18px] ${paragraphCount === 1 ? " speakable-intro" : ""}`}
                        id={element.id}
                      >
                        <InsertLineBreaks
                          content={ApplyParagraphAnnotations(
                            element.value,
                            element.annotations,
                          )}
                        />
                        {showPremiumLabel &&
                          paragraphCount ===
                            totalParagraphCountOfFullArticle && (
                            <img
                              src={PremiumIcon}
                              className="inline-block ml-2"
                              alt="Bagi Pelanggan Sahaja"
                              width={16}
                            />
                          )}
                      </Paragraph>
                    </ArticleElementWrapper>
                    {bannersAndAds}
                  </React.Fragment>
                );

              case "sub_head":
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <SubHead
                        element={element}
                        className="font-secondary text-md text-text-body leading-[150%] mb-6 lg:mb-8"
                      />
                    </ArticleElementWrapper>
                  </React.Fragment>
                );

              case "image": {
                const imageHeight =
                  element.image?.height && element.image.height >= 1460
                    ? 1460
                    : 550;
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <ArticleImage
                        index={index.toString()}
                        url={element.image?.url}
                        alt={element.caption}
                        caption={element.caption}
                        credit={element.credit}
                        height={imageHeight}
                        className="max-w-[860px] mb-6 lg:mb-8"
                      />
                    </ArticleElementWrapper>
                  </React.Fragment>
                );
              }

              case "embed":
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <NoSSR>
                        <Embed
                          className="my-6 lg:my-lg"
                          src={element.embedUrl}
                          {...(element.embedHeight && {
                            height: element.embedHeight,
                          })}
                        />
                      </NoSSR>
                    </ArticleElementWrapper>
                  </React.Fragment>
                );

              case "pull_quote":
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <PullQuoteCard
                        quote={element.quote}
                        quotee={element.quotee}
                        quoteLabel={element.quoteLabel}
                        quoteCredit={element.quoteCredit}
                      />
                    </ArticleElementWrapper>
                  </React.Fragment>
                );

              case "list_bulleted":
              case "list_numbered": {
                const listType = element.type === "list_bulleted" ? "ol" : "ul";
                const listItems = element.listItem;
                const listStyle =
                  element.listStyle === "decimal"
                    ? "list-decimal"
                    : "list-disc";
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <ParagraphList
                        listStyle={listStyle}
                        listType={listType}
                        listItems={listItems}
                      />
                    </ArticleElementWrapper>
                  </React.Fragment>
                );
              }

              case "youtube_video": {
                const url = `${YOUTUBE_WATCH_URL}${element.youtubeId}`;
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <YouTubeEmbedCustom url={url} className="my-lg" />
                    </ArticleElementWrapper>
                  </React.Fragment>
                );
              }

              case "brightcove_video": {
                return (
                  <React.Fragment key={index}>
                    <ArticleElementWrapper>
                      <WrapperContainer className="block my-lg">
                        <BrightcoveVideo videoId={element.videoId} />
                      </WrapperContainer>
                    </ArticleElementWrapper>
                  </React.Fragment>
                );
              }

              default:
                return null;
            }
          })}
      </div>

      {/* Access-control banners must remain outside paywall/regwall selector */}
      {hasAccessControlBanner &&
        getAdAndBannerInsertions({
          paragraphCount: accessBannerParagraphCount,
          ...insertionProps,
        })}

      <PostContentSection isCasScriptReady={isCasScriptReady} {...props} />
    </div>
  );
}
